import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { FileText, LayoutDashboard, Maximize2 } from 'lucide-react'

import { artifactHref } from '@atbash-labs/plantview-links'
import {
  useGetChatArtifactQuery,
  type ChatArtifact,
  type ChatArtifactKind
} from '@axilon-apps/domains-ontology-ui-state'

import { ARTIFACT_SANDBOX_WIDTH_PX } from '../../pages/ArtifactPage/artifact-measure'
import { ARTIFACT_BADGE_LABEL } from '../../pages/ArtifactPage/artifact-strings'
import {
  DELETED_DOCUMENT_MESSAGE,
  documentArtifactBody
} from '../../pages/ArtifactPage/document-artifact'
import DocumentArtifactDownloadButton from '../../pages/ArtifactPage/DocumentArtifactDownloadButton'
import DocFilePreview from '../../pages/DocsHubPage/DocFilePreview'
import { ChatMarkdown } from '../ChatMarkdown'
import SandboxFrame from '../DashboardSandbox/SandboxFrame'
import { useSandboxTemplateRegistry } from '../DashboardSandbox/useSandboxTemplateRegistry'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { useNearViewport } from './useNearViewport'

export interface ArtifactPreviewCardProps {
  artifactId: string
  title: string
  kind: ChatArtifactKind
  fetchMode: 'snapshot' | 'live'
}

export const INLINE_PREVIEW_HEIGHT_PX = 420

function unavailableStatus(error: unknown): boolean {
  if (error === null || typeof error !== 'object' || !('status' in error)) return false
  const status = (error as { status?: unknown }).status
  return status === 403 || status === 404
}

function DocumentPreview({ artifact }: { artifact: ChatArtifact }) {
  const body = documentArtifactBody(artifact)
  if (body.kind === 'file') {
    return (
      <DocFilePreview
        docId={body.documentId}
        revisionId={body.revisionId}
        name={artifact.title}
        format={body.format}
      />
    )
  }
  if (body.kind === 'deleted') {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-ink-3">
        {DELETED_DOCUMENT_MESSAGE}
      </div>
    )
  }
  return (
    <div
      data-testid={`artifact-preview-document-${artifact.id}`}
      className="h-full overflow-y-auto px-4 py-3"
    >
      <ChatMarkdown className="text-sm text-ink-2">{body.text}</ChatMarkdown>
    </div>
  )
}

export function ArtifactPreviewCard({
  artifactId,
  title,
  kind,
  fetchMode
}: ArtifactPreviewCardProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const near = useNearViewport(bodyRef)
  const [hasEnteredViewport, setHasEnteredViewport] = useState(near)
  useEffect(() => {
    if (near) setHasEnteredViewport(true)
  }, [near])
  const [bodyWidth, setBodyWidth] = useState(0)
  useLayoutEffect(() => {
    const el = bodyRef.current
    if (!el) return
    const measure = (): void => setBodyWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  // The sandbox lays out against its own viewport width, so the iframe renders
  // at the artifact page's sandbox width and is scaled down to fit the card.
  const scale = bodyWidth > 0 ? bodyWidth / ARTIFACT_SANDBOX_WIDTH_PX : 1
  const artifactQuery = useGetChatArtifactQuery(artifactId, { skip: !hasEnteredViewport })
  const templates = useSandboxTemplateRegistry()
  const unavailable = unavailableStatus(artifactQuery.error)
  const displayTitle = artifactQuery.data?.title ?? title
  const displayFetchMode = artifactQuery.data?.fetch_mode ?? fetchMode
  const isDocument = kind === 'document'
  const KindIcon = isDocument ? FileText : LayoutDashboard

  return (
    <div className="flex min-w-0 flex-col rounded-md border border-line bg-card">
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
        <KindIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 truncate font-medium text-foreground">{displayTitle}</span>
        <Badge variant="secondary">{ARTIFACT_BADGE_LABEL}</Badge>
        {!isDocument && displayFetchMode === 'live' ? (
          <Badge variant="secondary">Live</Badge>
        ) : null}
        {unavailable ? (
          <span className="text-muted-foreground">no longer available</span>
        ) : (
          <>
            <div className="flex-1" />
            {isDocument && artifactQuery.data ? (
              <DocumentArtifactDownloadButton
                artifact={artifactQuery.data}
                variant="ghost"
                className="text-muted-foreground"
              />
            ) : null}
            <Button asChild variant="ghost" size="xs" className="-mr-1.5 text-muted-foreground">
              <RouterLink to={artifactHref(artifactId)}>
                <Maximize2 aria-hidden />
                Open
              </RouterLink>
            </Button>
          </>
        )}
      </div>
      {unavailable ? null : (
        <div
          ref={bodyRef}
          data-testid={`artifact-preview-body-${artifactId}`}
          className="relative w-full overflow-hidden rounded-b-md bg-bg-sunk"
          style={{ height: INLINE_PREVIEW_HEIGHT_PX }}
        >
          {hasEnteredViewport && artifactQuery.data && isDocument ? (
            <DocumentPreview artifact={artifactQuery.data} />
          ) : hasEnteredViewport && artifactQuery.data ? (
            <div
              data-testid={`artifact-preview-scale-${artifactId}`}
              className="origin-top-left"
              style={{
                width: ARTIFACT_SANDBOX_WIDTH_PX,
                height: INLINE_PREVIEW_HEIGHT_PX / scale,
                transform: `scale(${scale})`
              }}
            >
              <SandboxFrame code={artifactQuery.data.code} templates={templates} />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-ink-3">
              {displayTitle}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
